import { LicenseDetails } from "@/components/license-details";
import { RegistrationDetailsSheetQuerySkeletons } from "@/components/license-details/registration-details-section-loading";
import PageHeader from "@/components/page-header";
import { ContextHelpButton } from "@/components/registrant/help/context-help-button";
import { Button } from "@/components/ui/button";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import { UserType } from "@/constants";
import {
  getLicenseCommentsOptions,
  getLicensePaymentsOptions,
  getLicenseTypeQuestionsOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import type {
  LicenseCommentsResponseSchema,
  LicensePaymentsResponseSchema,
} from "@/hera-api/client/types.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useCommentHandlers } from "@/hooks/use-comment-handlers";
import {
  type LicenseDetailsDisplayMode,
  useLicenseDetailsDisplayMode,
} from "@/hooks/use-license-details-display-mode";
import { useLicenseDetailsWithRuntimeRefresh } from "@/hooks/use-license-details-with-runtime-refresh";
import { setLicenseDisplayedSnapshot } from "@/lib/license-displayed-snapshot";
import { cn, getQueryErrorHeading, getQueryErrorMessage } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Maximize2, PanelRight, X } from "lucide-react";
import { useEffect } from "react";
import { validate as uuidValidate } from "uuid";

const EMPTY_COMMENTS: LicenseCommentsResponseSchema = { comments: [] };
const EMPTY_PAYMENTS: LicensePaymentsResponseSchema = { payments: [] };

export const Route = createFileRoute("/registrant/licenses/$licenseId/view")({
  component: ViewLicenseScreen,
  validateSearch: (
    search: Record<string, unknown>,
  ): { section?: "property"; display?: LicenseDetailsDisplayMode } => ({
    section: search.section === "property" ? ("property" as const) : undefined,
    display:
      search.display === "fullscreen" || search.display === "pane"
        ? search.display
        : undefined,
  }),
});

export function ViewLicenseScreen() {
  const { licenseId } = Route.useParams();
  const { section, display } = Route.useSearch();
  const navigate = useNavigate();
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const commentHandlers = useCommentHandlers({
    licenseId,
    userType: UserType.REGISTRANT,
  });
  const { displayMode, toggleDisplayMode } =
    useLicenseDetailsDisplayMode(display);
  const isFullscreen = displayMode === "fullscreen";
  const isValidLicenseId = uuidValidate(licenseId);

  useEffect(() => {
    if (!isValidLicenseId) return;
    setLicenseDisplayedSnapshot({ openLicenseId: licenseId });
    return () => {
      setLicenseDisplayedSnapshot({ openLicenseId: null });
    };
  }, [isValidLicenseId, licenseId]);

  const {
    licenseData,
    isPreviewLoading,
    isRuntimeRefreshing,
    previewError: queryError,
  } = useLicenseDetailsWithRuntimeRefresh({
    licenseId,
    enabled: isAuthSetupComplete && isValidLicenseId,
  });

  const { data: payments, isLoading: isPaymentsQueryLoading } = useQuery({
    ...getLicensePaymentsOptions({
      path: { license_id: licenseId },
    }),
    enabled: isAuthSetupComplete && isValidLicenseId,
  });

  const { data: comments, isLoading: isCommentsQueryLoading } = useQuery({
    ...getLicenseCommentsOptions({
      path: { license_id: licenseId },
    }),
    enabled: isAuthSetupComplete && isValidLicenseId,
  });

  const {
    data: additionalQuestions,
    isLoading: isAdditionalQuestionsQueryLoading,
  } = useQuery({
    ...getLicenseTypeQuestionsOptions({
      path: {
        license_type_id: licenseData?.license_type.id as string,
        jurisdiction_id: licenseData?.jurisdiction.id as string,
      },
    }),
    enabled:
      isAuthSetupComplete &&
      isValidLicenseId &&
      !isPreviewLoading &&
      !!licenseData,
  });

  if (!isValidLicenseId) {
    return (
      <ErrorComponent
        title="Invalid URL"
        message={`The license ID '${licenseId}' is not a valid license ID`}
      />
    );
  }
  if (!isAuthSetupComplete) {
    return <LoadingScreen message="Authenticating..." />;
  }

  if (queryError) {
    return (
      <ErrorComponent
        title={getQueryErrorHeading(queryError)}
        message={getQueryErrorMessage(queryError)}
      />
    );
  }

  return (
    <div
      className={cn("w-full space-y-6", !isFullscreen && "ml-auto sm:max-w-xl")}
    >
      <PageHeader
        title="License Details"
        hideBackButton
        leadingAction={
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={toggleDisplayMode}
            aria-label={
              isFullscreen
                ? "Show license details in right pane"
                : "Show license details full screen"
            }
            title={isFullscreen ? "Right pane" : "Full screen"}
          >
            {isFullscreen ? <PanelRight /> : <Maximize2 />}
          </Button>
        }
        action={
          <div className="flex items-center gap-2">
            <ContextHelpButton articleId="registration-details" />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => void navigate({ to: "/registrant/dashboard" })}
              aria-label="Close"
              title="Close"
            >
              <X />
            </Button>
          </div>
        }
      />
      {!licenseData && isPreviewLoading ? (
        <RegistrationDetailsSheetQuerySkeletons />
      ) : licenseData ? (
        <LicenseDetails
          userType={UserType.REGISTRANT}
          data={licenseData}
          commentsData={comments ?? EMPTY_COMMENTS}
          paymentsData={payments ?? EMPTY_PAYMENTS}
          additionalQuestions={additionalQuestions}
          paymentsLoading={isPaymentsQueryLoading}
          commentsLoading={isCommentsQueryLoading}
          additionalQuestionsLoading={isAdditionalQuestionsQueryLoading}
          isLicenseRuntimeRefreshing={isRuntimeRefreshing}
          initialFocusSection={section === "property" ? "property" : null}
          onRegistrantComment={commentHandlers.handleRegistrantComment}
        />
      ) : null}
    </div>
  );
}
